import Big from 'big.js';
import {combineLatest} from 'rxjs';
import {throttleTime, map, startWith} from 'rxjs/operators';
import {div, gt, max, min, minus, plus, times} from '@/helper/num';
import {contractUtil} from '@/helper/contract';
import {calcMaintenanceMargin} from '@/helper/leverage';
import {calcValueEv, calcValueRv} from '@/calc';
import {positionsCache$} from '@/positions/positions-cache';
import {contractMarketRealtimeCache$} from '@/market-contract/market24h-contract-cache';
import {contractDefaultTakerFeeRateEr$, contractsCache$, leverageMarginsCache$} from '@/config';
import type {ws, ContractMarket, ContractPair, LeverageMargin, PosSide} from '@/types';

type ContractPositionRaw = ws.ContractPositionRaw;

export interface AccountPosTotalData {
  valueFactor: number;
  totalCrossPosCost: Big;
  totalCrossFixedUsedBalance: Big;
  totalOrderUsedBalance: Big;
  totalIsolatedTotalBalance: Big;
  totalPosMarginEv: Big;
}

export const accountPosTotalDataCache$ = combineLatest([
  positionsCache$,
  contractMarketRealtimeCache$,
  contractsCache$,
  leverageMarginsCache$,
  contractDefaultTakerFeeRateEr$,
]).pipe(throttleTime(500), map(calcPostTotalData), startWith(new Map<string, AccountPosTotalData>()));

export function calcPostTotalData([positionsCache, marketsCache, contractsCache, marginCache, defaultTakerFeeEr]: [
  Map<string, ContractPositionRaw>,
  Map<string, ContractMarket>,
  Map<string, ContractPair>,
  Map<string, LeverageMargin>,
  number
]): Map<string, AccountPosTotalData> {
  const positions = positionsCache.toArray<ContractPositionRaw>();
  const settleCurrencies = Array.from(new Set(positions.map((o) => o.currency)));
  return settleCurrencies.reduce((p, c) => {
    const positions2 = positions.filter(({currency, size, isCross}) => currency === c && isCross && size > 0);
    const totalCrossPosCost =
      positions2.length > 0
        ? calcAccountCrossPosCost(positions2, marketsCache, contractsCache, defaultTakerFeeEr)
        : Big(0);
    const totalPosMarginEv = positions2.length > 0 ? calcTotalCrossPosMarginEv(positions2) : Big(0);

    const positions3 = positions.filter(({currency, posMode, posSide, type}) => {
      if (currency !== c) {
        return false;
      }
      return (
        type === 'Perpetual' ||
        (posMode === 'OneWay' && posSide === 'Merged') ||
        (posMode === 'Hedged' && ['Long', 'Short'].includes(posSide))
      );
    });
    const totalOrderUsedBalance =
      positions3.length > 0 ? calcOrdUsedBalance(positions3, marketsCache, contractsCache) : Big(0);

    const positions4 = positions.filter(
      ({currency, isCross, size, posMode, posSide}) =>
        currency === c && isCross && size > 0 && posMode === 'Hedged' && ['Long', 'Short'].includes(posSide)
    );
    const totalCrossFixedUsedBalance =
      positions4.length > 0
        ? calcTotalFixedUsedBalance(positions4, contractsCache, marginCache, defaultTakerFeeEr)
        : Big(0);

    const positions5 = positions.filter(({currency, size, isCross}) => currency === c && !isCross && size > 0);
    const totalIsolatedTotalBalance = positions5.length > 0 ? calcIsolatedPosCost(positions5) : Big(0);

    const contract = contractsCache.toArray<ContractPair>().find(({settleCurrency}) => settleCurrency === c);
    if (contract) {
      const {contractSide, valueFactor} = contract;
      if (contractSide < 0) {
        p.set(c, {
          valueFactor: valueFactor,
          totalCrossPosCost: totalCrossPosCost.div(valueFactor),
          totalCrossFixedUsedBalance: totalCrossFixedUsedBalance.div(valueFactor),
          totalOrderUsedBalance: totalOrderUsedBalance.div(valueFactor),
          totalIsolatedTotalBalance: totalIsolatedTotalBalance.div(valueFactor),
          totalPosMarginEv,
        });
        return p;
      }
    }
    p.set(c, {
      totalCrossPosCost,
      totalCrossFixedUsedBalance,
      totalOrderUsedBalance,
      totalIsolatedTotalBalance,
      totalPosMarginEv,
      valueFactor: 1,
    });
    return p;
  }, new Map());
}

// 计算全仓仓位占用
export function calcCrossPosCost(
  positions: ContractPositionRaw[],
  market: ContractMarket,
  contract: ContractPair,
  defaultTakerFeeEr: number,
  posSide: PosSide = null
) {
  const isOneWay = positions.every(({posMode}) => posMode === 'OneWay');
  if (!contract || !market) {
    return Big(0);
  }
  const markPrice = market ? Big(market.markPriceEp || 0) : 0;
  const defaultTakerFeeEr2 = contractUtil.getDefaultTakerFeeRate(contract, defaultTakerFeeEr);

  if (isOneWay) {
    const position = positions.find(({posMode, posSide}) => posMode === 'OneWay' && posSide === 'Merged');
    if (!position) {
      return Big(0);
    }
    const {side, size, buyValueToCostEr, sellValueToCostEr} = position;
    const markValue =
      size && markPrice
        ? calcValueEv(
            contract.contractSide,
            contract.contractSize,
            contract.priceFactor,
            contract.valueFactor,
            size,
            markPrice
          )
        : Big(0);
    const valueToCostEr = side === 'Buy' ? buyValueToCostEr : sellValueToCostEr;
    return Big(markValue).times(Big(valueToCostEr).div(contract.ratioFactor).minus(defaultTakerFeeEr2));
  } else {
    const posLong = positions.find(({posMode, posSide}) => posMode === 'Hedged' && posSide === 'Long');
    const posShort = positions.find(({posMode, posSide}) => posMode === 'Hedged' && posSide === 'Short');
    const longSize = posLong?.size || 0;
    const shortSize = posShort?.size || 0;
    const morePosition = longSize > shortSize ? posLong : posShort;

    if (!morePosition) {
      return Big(0);
    }
    let netPartSize = Big(0);
    if (!posSide || morePosition.posSide === posSide) {
      netPartSize = Big(longSize).minus(shortSize).abs();
    }
    const netPartValue = netPartSize.gt(0)
      ? Big(
          calcValueEv(
            contract.contractSide,
            contract.contractSize,
            contract.priceFactor,
            contract.valueFactor,
            netPartSize,
            markPrice
          )
        )
      : Big(0);
    const {side, buyValueToCostEr, sellValueToCostEr} = morePosition;
    const valueToCostEr = side === 'Buy' ? buyValueToCostEr : sellValueToCostEr;
    return netPartValue.times(Big(valueToCostEr).div(contract.ratioFactor).minus(defaultTakerFeeEr2));
  }
}

// 计算账户全仓占用
export function calcAccountCrossPosCost(
  positions: ContractPositionRaw[],
  marketsCache: Map<string, ContractMarket>,
  contractsCache: Map<string, ContractPair>,
  defaultTakerFeeEr: number
) {
  const symbols = Array.from(new Set(positions.map(({symbol}) => symbol)));
  return symbols
    .map((symbol) => {
      const curPositions = positions.filter((item) => item.symbol === symbol);
      const contract = contractsCache.get(symbol);
      const market = marketsCache.get(symbol);
      return calcCrossPosCost(curPositions, market, contract, defaultTakerFeeEr);
    })
    .reduce((p, c) => Big(p).plus(c), Big(0));
}

// 计算同一结算币种的订单占用: https://cmex-pro.jp.larksuite.com/wiki/G6tJwCJSbi3EOsk59X6jUJ8QpBc
function calcOrdUsedBalance(
  positions: ContractPositionRaw[],
  marketsCache: Map<string, ContractMarket>,
  contractsCache: Map<string, ContractPair>
) {
  return positions
    .map((position) => {
      const {symbol} = position;
      if (!contractsCache.has(symbol)) {
        return '0';
      }
      const {ratioFactor, priceFactor, contractSide, contractSize, valueFactor} = contractsCache.get(symbol);
      const marketPrice = marketsCache.get(symbol);

      const priceRp = marketPrice ? marketPrice.markPriceEp : 0;
      const {
        buyLeavesValueEv,
        sellLeavesValueEv,
        buyLeavesQty,
        sellLeavesQty,
        buyValueToCostEr,
        sellValueToCostEr,
        size,
      } = position;
      const buySize = position.side === 'Buy' ? size : 0;
      const sellSize = position.side === 'Buy' ? 0 : size;
      const buyOrderUsed = calcPosSideOrd(
        true,
        contractSide,
        contractSize,
        buyLeavesValueEv,
        buyLeavesQty,
        buyValueToCostEr,
        sellSize,
        priceRp,
        ratioFactor,
        priceFactor,
        valueFactor
      );

      const sellOrderUsed = calcPosSideOrd(
        false,
        contractSide,
        contractSize,
        sellLeavesValueEv,
        sellLeavesQty,
        sellValueToCostEr,
        buySize,
        priceRp,
        ratioFactor,
        priceFactor,
        valueFactor
      );

      return max(buyOrderUsed, sellOrderUsed);
    })
    .reduce((p, c) => Big(p).plus(c), Big(0));
}

// 计算同一结算币种的Fully Hedged 部分仓位的仓位占用：https://confluence.cmex.corp/pages/viewpage.action?pageId=97355538
function calcTotalFixedUsedBalance(
  positions: ContractPositionRaw[],
  contractsCache: Map<string, ContractPair>,
  marginCache: Map<string, LeverageMargin>,
  defaultTakerFeeEr: number
) {
  const symbols = Array.from(new Set(positions.map(({symbol}) => symbol)));
  const totalHedgedMm = symbols
    .map((symbol) => {
      const curPositions = positions.filter((item) => item.symbol === symbol);
      if (curPositions.length > 1) {
        const posLong = curPositions.find(({posMode, posSide}) => posMode === 'Hedged' && posSide === 'Long');
        const posShort = curPositions.find(({posMode, posSide}) => posMode === 'Hedged' && posSide === 'Short');

        const longSize = posLong?.size || 0;
        const shortSize = posShort?.size || 0;
        const lessPosition = longSize > shortSize ? posShort : posLong;

        if (lessPosition) {
          return calcFixedUsedBalance(symbol, lessPosition, contractsCache, marginCache, defaultTakerFeeEr);
        }
      }
      return 0;
    })
    .reduce((p, c) => Big(p).plus(c), Big(0));
  return totalHedgedMm.times(2); // 存在long与short仓位，所以 * 2
}

// 计算同一结算币种的逐仓保证金: https://cmex-pro.jp.larksuite.com/wiki/G6tJwCJSbi3EOsk59X6jUJ8QpBc
function calcIsolatedPosCost(positions: ContractPositionRaw[]) {
  return positions.reduce((p, c) => p.plus(c.assignedPosBalanceEv), Big(0));
}

function calcPosSideOrd(
  isBuy,
  contractSide,
  contractSize,
  leavesValueEv,
  leavesQty,
  valueToCostEr,
  size,
  priceEp,
  ratioFactor,
  priceFactor,
  valueFactor
) {
  if (gt(leavesQty, 0)) {
    const valueToCost = div(valueToCostEr, ratioFactor);
    const netOrderQty = max(0, leavesQty - size);
    const netOrderValue = times(leavesValueEv, div(netOrderQty, leavesQty));
    const orderCost = times(netOrderValue, valueToCost);
    const orderLostValue = calcValueEv(contractSide, contractSize, priceFactor, valueFactor, netOrderQty, priceEp);
    let orderLost;
    if (contractSide > 0) {
      orderLost = max(0, isBuy ? minus(netOrderValue, orderLostValue) : minus(orderLostValue, netOrderValue));
    } else {
      orderLost = max(0, isBuy ? minus(orderLostValue, netOrderValue) : minus(netOrderValue, orderLostValue));
    }
    return plus(orderCost, orderLost);
  }
  return '0';
}

function calcTotalCrossPosMarginEv(positions: ContractPositionRaw[]) {
  const symbols = Array.from(new Set(positions.map(({symbol}) => symbol)));
  return symbols
    .map((symbol) => {
      const curPositions = positions.filter((item) => item.symbol === symbol && item.size > 0);
      if (curPositions.length > 1) {
        const morePosition = curPositions[0].size > curPositions[1].size ? curPositions[0] : curPositions[1];
        return morePosition.crossMarginWeightEv;
      }
      return curPositions[0].crossMarginWeightEv || 0;
    })
    .reduce((p, c) => Big(p).plus(c), Big(0));
}

export function calcFixedUsedBalance(
  symbol,
  position: ContractPositionRaw,
  contractsCache: Map<string, ContractPair>,
  marginCache: Map<string, LeverageMargin>,
  defaultTakerFeeEr: number
) {
  const contract = contractsCache.get(symbol);
  const {maintenanceMargin} = calcMaintenanceMargin(
    contract,
    {...position, markPriceEp: position.avgEntryPriceEp},
    marginCache
  );
  const {buyValueToCostEr, sellValueToCostEr, initMarginReqEr, posSide, size} = position;
  const valueToCostEr = posSide === 'Long' ? buyValueToCostEr : sellValueToCostEr;
  const posValue = calcValueRv(
    contract.contractSide,
    contract.contractSize,
    contract.priceFactor,
    size,
    position.avgEntryPriceEp
  );
  const closePosFeeRate = Big(valueToCostEr).minus(initMarginReqEr).minus(defaultTakerFeeEr);
  return plus(times(maintenanceMargin, 1.2), Big(posValue).times(closePosFeeRate));
}
